// client/src/pages/Game/Battlefield/components/MulliganScreen.jsx

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import Button from '../../../../components/ui/Button';
import CardPreview from './CardPreview';
import './MulliganScreen.css';

/**
 * Position d'origine des cartes : le deck du joueur sur le battlefield
 * (derrière l'overlay). Fallback géométrique si introuvable.
 */
const getDeckOrigin = () => {
  const deckEl = document.querySelector('.player-deck');
  const r = deckEl?.getBoundingClientRect();
  if (r && r.width > 0) {
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }
  return { x: 90, y: window.innerHeight - 110 };
};

const centerOf = (el) => {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
};

const MulliganScreen = ({
  cards = [],
  onConfirm,
  getCardElement = (card) => card.element?.toLowerCase() || 'fire'
}) => {
  // Créer des identifiants uniques pour chaque carte (ID + index)
  const cardsWithUniqueIds = cards.map((card, index) => ({
    ...card,
    uniqueId: `${card.id}_${index}`
  }));

  const [selectedUniqueIds, setSelectedUniqueIds] = useState([]);
  const [previewCard, setPreviewCard] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const cardElsRef = useRef({}); // uniqueId → élément DOM de la carte

  useEffect(() => {
    // Réinitialiser les cartes sélectionnées quand les cartes changent
    setSelectedUniqueIds([]);
  }, [cards]);

  // ── Animation d'ouverture : la main de départ SE PIOCHE du deck ──
  // Chaque carte vole du deck vers son emplacement, en cascade.
  useLayoutEffect(() => {
    const origin = getDeckOrigin();
    cardsWithUniqueIds.forEach((card, i) => {
      const el = cardElsRef.current[card.uniqueId];
      if (!el) return;
      const c = centerOf(el);
      const dx = origin.x - c.x;
      const dy = origin.y - c.y;
      el.animate([
        {
          transform: `translate(${dx}px, ${dy}px) rotate(${i % 2 === 0 ? -10 : 10}deg) scale(0.7)`,
          opacity: 0.25,
          filter: 'brightness(0.5) blur(1px)',
        },
        {
          transform: 'translate(0, 0) rotate(0deg) scale(1)',
          opacity: 1,
          filter: 'brightness(1) blur(0)',
        },
      ], {
        duration: 480,
        delay: 130 * i,
        easing: 'cubic-bezier(0.22, 0.9, 0.32, 1)',
        fill: 'backwards',
      });
    });
    // Uniquement au montage — l'animation ne se rejoue pas à chaque render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards.length === 0 ? 'empty' : cards.map(c => c.id).join('|')]);

  // Gérer la sélection/désélection d'une carte pour le mulligan
  const handleCardToggle = (card) => {
    if (isConfirming) return; // Empêcher le toggle pendant l'animation de confirmation

    if (selectedUniqueIds.includes(card.uniqueId)) {
      setSelectedUniqueIds(selectedUniqueIds.filter(id => id !== card.uniqueId));
    } else {
      setSelectedUniqueIds([...selectedUniqueIds, card.uniqueId]);
    }
  };

  // Afficher la prévisualisation d'une carte
  const handleCardPreview = (card) => {
    if (isConfirming) return; // Empêcher la prévisualisation pendant l'animation
    setPreviewCard(card);
  };

  // Fermer la prévisualisation
  const handleClosePreview = () => {
    setPreviewCard(null);
  };

  // Confirmer le mulligan avec animation :
  // 1. les cartes sélectionnées retournent DANS le deck (vol + fondu)
  // 2. les cartes conservées pulsent en vert
  // 3. le serveur est appelé — les nouvelles cartes arriveront en volant
  //    depuis le deck (animation de pioche côté PlayerHand)
  const handleConfirm = () => {
    if (isConfirming) return;
    setIsConfirming(true);

    const origin = getDeckOrigin();

    cardsWithUniqueIds.forEach((card) => {
      const el = cardElsRef.current[card.uniqueId];
      if (!el) return;
      const isSelected = selectedUniqueIds.includes(card.uniqueId);

      if (isSelected) {
        // Retour au deck : la carte s'envole vers le deck et s'y glisse
        const c = centerOf(el);
        const dx = origin.x - c.x;
        const dy = origin.y - c.y;
        el.style.zIndex = 50;
        el.animate([
          { transform: 'translate(0, 0) rotate(0deg) scale(1)', opacity: 1 },
          { transform: `translate(${dx * 0.35}px, ${dy * 0.35}px) rotate(${dx > 0 ? 12 : -12}deg) scale(0.9)`, opacity: 1, offset: 0.35 },
          { transform: `translate(${dx}px, ${dy}px) rotate(${dx > 0 ? 24 : -24}deg) scale(0.35)`, opacity: 0 },
        ], {
          duration: 560,
          easing: 'cubic-bezier(0.5, 0.05, 0.7, 0.4)',
          fill: 'forwards',
        });
      }
    });

    // Les cartes conservées pulsent en vert via la classe CSS confirming-keep.
    // Attendre la fin du vol avant d'appeler le serveur.
    setTimeout(() => {
      if (onConfirm) {
        onConfirm(
          cardsWithUniqueIds
            .filter(card => selectedUniqueIds.includes(card.uniqueId))
            .map(card => card.id)
        );
      }
    }, 700);
  };

  // Déterminer la classe CSS pour chaque carte (pour l'animation)
  const getCardClass = (card) => {
    let className = 'mulligan-card';

    if (selectedUniqueIds.includes(card.uniqueId)) {
      className += ' selected';
      if (isConfirming) {
        className += ' confirming-replace';
      }
    } else if (isConfirming) {
      className += ' confirming-keep';
    }

    return className;
  };

  return (
    <div className="mulligan-screen">
      <div className="mulligan-overlay" onClick={handleClosePreview}></div>

      {/* Zone de prévisualisation de carte */}
      {previewCard && (
        <div className="card-preview-wrapper">
          <button className="card-preview-close-button" onClick={handleClosePreview}>×</button>
          <CardPreview
            card={previewCard}
            getCardElement={getCardElement}
          />
        </div>
      )}

      <div className="mulligan-container">
        <h2>Mulligan</h2>
        <p>Sélectionnez les cartes que vous souhaitez remplacer dans votre main de départ</p>

        <div className="mulligan-cards">
          {cardsWithUniqueIds.map((card, index) => (
            <div
              key={card.uniqueId}
              className={getCardClass(card)}
              ref={(el) => {
                if (el) cardElsRef.current[card.uniqueId] = el;
                else delete cardElsRef.current[card.uniqueId];
              }}
            >
              <img
                src={card.imageUrl || `/api/static/images/cards/${getCardElement(card)}.svg`}
                alt={card.name || 'Card'}
                onClick={() => handleCardPreview(card)}
                onError={(e) => {
                  e.target.src = `/api/static/images/card-backs/neutral.webp`;
                }}
              />
              {/* Afficher le coût de la carte */}
              <div className="mulligan-card-cost">
                {card.cost || '?'}
              </div>
              <button
                className="select-button"
                onClick={() => handleCardToggle(card)}
              >
                {selectedUniqueIds.includes(card.uniqueId) ? (isConfirming ? '✕' : '-') : (isConfirming ? '✓' : '-')}
              </button>
            </div>
          ))}
        </div>

        <div className="mulligan-actions">
          <Button
            type="primary"
            onClick={handleConfirm}
            className="confirm-button"
            disabled={isConfirming}
          >
            {isConfirming ? 'Traitement en cours...' : `Confirmer ${selectedUniqueIds.length > 0 ? `(${selectedUniqueIds.length} cartes)` : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MulliganScreen;
